import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FaWindowClose, FaEye, FaEdit } from "react-icons/fa";
import ReactQuill, { Quill } from "react-quill";
import CommonModal from "./CommonModal";
import {
  catchError,
  byteCount,
  changeState,
  commonModalSetting,
} from "JS/common";
import { uploadNoticeImg, updateFaq, createFaq } from "JS/API";
import "react-quill/dist/quill.snow.css";
import ImageResize from "quill-image-resize-module-react";

Quill.register("modules/imageResize", ImageResize);

const FaqWriteModal = ({
  selectedFaq, // id 대신 selectedFaq 객체를 받음
  setOpenWriteFaq,
  setAlert,
  setAlertBox,
  getList,
}) => {
  const [info, setInfo] = useState({
    question: "",
    answer: "",
    category: "",
    is_active: true,
  });
  const [byte, setByte] = useState({
    question: 0,
    answer: 0,
  });
  const [localAlert, setLocalAlert] = useState("");
  const [localAlertBox, setLocalAlertBox] = useState({
    mode: "",
    context: "",
    bool: false,
  });
  const [mode, setMode] = useState("write");
  const [, setUploadedImagePath] = useState("");
  const [isPreviewMode, setIsPreviewMode] = useState(false); // 미리보기 모드 상태
  const navigate = useNavigate();
  const quillRef = useRef(null);


  const imageHandler = () => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();

    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        try {
          const result = await uploadNoticeImg(file);

          console.log("서버 응답:", result);

          if (typeof result === "object" && result.data) {
            const responseData = Array.isArray(result.data)
              ? result.data
              : [result.data];

            if (responseData.length > 0 && responseData[0].path) {
              const imagePath = responseData[0].path;
              console.log("추출된 이미지 경로:", imagePath);

              setUploadedImagePath(imagePath);

              const quill = quillRef.current.getEditor();
              const range = quill.getSelection(true);

              // 이미지 삽입 (Delta로 인식)
              quill.insertEmbed(range.index, "image", imagePath);

              // 줄바꿈 삽입 (다음 줄 확보)
              quill.insertText(range.index + 1, "\n");

              // 커서 이동
              quill.setSelection(range.index + 2);
            } else {
              console.error("이미지 경로를 찾을 수 없습니다:", responseData);
              throw new Error("이미지 URL을 찾을 수 없습니다");
            }
          } else {
            console.error("잘못된 응답 형식:", result);
            throw new Error("서버 응답이 올바르지 않습니다");
          }
        } catch (error) {
          console.error("이미지 업로드 중 오류 발생:", error);
          commonModalSetting(
            setLocalAlertBox,
            true,
            "alert",
            "이미지 업로드 중 오류가 발생했습니다."
          );
        }
      }
    };
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["image"],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ list: "bullet" }],
      [{ indent: "-1" }, { indent: "+1" }],
      [{ align: [] }],
      ["link"],
    ],
    imageResize: {
      parchment: Quill.import("parchment"),
      modules: ["Resize", "DisplaySize"],
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "image",
    "list",
    "bullet",
    "indent",
    "align",
    "blockquote",
    "link",
  ];

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const toolbar = quill.getModule("toolbar");
      toolbar.addHandler("image", imageHandler);
    }
  }, [quillRef.current]);

  // selectedFaq가 있으면 수정 모드, 없으면 새 작성 모드
  useEffect(() => {
    if (selectedFaq) {
      setMode("edit");
      setInfo({
        question: selectedFaq.question || "",
        answer: selectedFaq.answer || "", // 일단 기본값으로 설정
        category: selectedFaq.category || "",
        is_active:
          selectedFaq.is_active !== undefined ? selectedFaq.is_active : true,
      });

      // 에디터가 준비된 후 HTML을 직접 설정
      if (quillRef.current && selectedFaq.answer) {
        setTimeout(() => {
          const quill = quillRef.current.getEditor();

          // 방법 1: innerHTML로 직접 설정
          quill.root.innerHTML = selectedFaq.answer;

          // 방법 2: 또는 clipboard.dangerouslyPasteHTML 사용
          // quill.clipboard.dangerouslyPasteHTML(selectedFaq.answer);

          // 에디터 히스토리 초기화 (undo/redo 방지)
          quill.history.clear();

          console.log("HTML 직접 설정 완료:", selectedFaq.answer);

          // 리사이즈 모듈 재초기화
          setTimeout(() => {
            const imageResize = quill.getModule("imageResize");
            if (imageResize) {
              const images = quill.root.querySelectorAll("img");
              images.forEach((img) => {
                // 각 이미지에 리사이즈 기능 재적용
                if (imageResize.handleClick) {
                  img.addEventListener("click", () =>
                    imageResize.handleClick(img)
                  );
                }
              });
            }
          }, 100);
        }, 100); // 에디터가 완전히 렌더링된 후 실행
      }
    } else {
      setMode("write");
      setInfo({
        question: "",
        answer: "",
        category: "",
        is_active: true,
      });
    }
  }, [selectedFaq]);

  //= FAQ 수정
  const editFaq = async () => {
    // 입력 검증
    if (!info.question && !info.answer) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "제목과 내용을 입력해 주세요."
      );
    } else if (!info.question) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "제목을 입력해 주세요."
      );
    } else if (!info.answer) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "내용을 입력해 주세요."
      );
    } else if (!info.category) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "카테고리를 선택해 주세요."
      );
    }

    const editor = quillRef.current.getEditor();
    const editorHtml = editor.root.innerHTML;
    const editorDelta = editor.getContents();

    const data = {
      category: info.category,
      question: info.question,
      answer: editorHtml,
      answer_delta: JSON.stringify(editorDelta),
      is_active: info.is_active,
    };

    const result = await updateFaq(selectedFaq.id, data); // selectedFaq.id 사용
    if (typeof result === "object") {
      setLocalAlert("completeEdit");
      commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "성공적으로 수정 되었습니다."
      );
    } else return catchError(result, navigate, setLocalAlertBox, setLocalAlert);
  };

  //= FAQ 새 작성
  const writeFaq = async () => {
    // 입력 검증
    if (!info.question && !info.answer) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "제목과 내용을 입력해 주세요."
      );
    } else if (!info.question) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "제목을 입력해 주세요."
      );
    } else if (!info.answer) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "내용을 입력해 주세요."
      );
    } else if (!info.category) {
      return commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "카테고리를 선택해 주세요."
      );
    }

    const editor = quillRef.current.getEditor();
    const editorHtml = editor.root.innerHTML;
    const editorDelta = editor.getContents();

    const data = {
      category: info.category,
      question: info.question,
      answer: editorHtml,
      answer_delta: JSON.stringify(editorDelta),
      is_active: info.is_active,
    };

    const result = await createFaq(data);
    if (typeof result === "object") {
      setLocalAlert("completeApply");
      commonModalSetting(
        setLocalAlertBox,
        true,
        "alert",
        "성공적으로 등록 되었습니다."
      );
    } else {
      return catchError(result, navigate, setLocalAlertBox, setLocalAlert);
    }
  };

  // 모달 닫기 확인
  const handleCloseModal = () => {
    setLocalAlert("confirmCancel");
    commonModalSetting(
      setLocalAlertBox,
      true,
      "confirm",
      `정말 취소하시겠습니까?<br />지금까지 ${
        mode === "edit" ? "수정" : "작성"
      }된 내용은 반영되지 않습니다.`
    );
  };

  // 완료 버튼 핸들러
  const handleSubmit = () => {
    if (selectedFaq) {
      // 수정 모드
      editFaq();
    } else {
      // 새 작성 모드
      writeFaq();
    }
  };

  // 미리보기 모드 토글
  const togglePreviewMode = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  // HTML 콘텐츠를 안전하게 렌더링하기 위한 함수
  const getPreviewContent = () => {
    if (quillRef.current) {
      const editor = quillRef.current.getEditor();
      return editor.root.innerHTML;
    }
    return "";
  };

  useEffect(() => {
    byteCount(info.answer, setInfo, setByte, "answer", 3000);
  }, [info.answer]);

  useEffect(() => {
    byteCount(info.question, setInfo, setByte, "question", 300);
  }, [info.question]);

  useEffect(() => {
    if (quillRef.current && info.answer) {
      const quill = quillRef.current.getEditor();

      // 에디터 내용이 로드된 후 이미지 리사이즈 모듈 재초기화
      setTimeout(() => {
        const images = quill.container.querySelectorAll("img");
        images.forEach((img) => {
          // 이미지 리사이즈 핸들러 재적용
          if (img.style.width || img.style.height) {
            img.setAttribute("data-resize-applied", "true");
          }
        });

        // 리사이즈 모듈 강제 재초기화
        const imageResize = quill.getModule("imageResize");
        if (imageResize) {
          imageResize.repositionElements();
        }
      }, 100);
    }
  }, [info.answer]);

  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const editorRoot = quill.root;

    const handleKeyDown = (e) => {
      if (e.key === "Enter") {
        const selection = quill.getSelection();
        if (!selection) return;

        // 현재 선택된 요소가 이미지인 경우
        const [leaf] = quill.getLeaf(selection.index);
        const node = leaf?.domNode;

        if (node?.tagName === "IMG") {
          e.preventDefault();
          e.stopPropagation();

          // 이미지 아래 줄바꿈 삽입
          quill.insertText(selection.index + 1, "\n");
          quill.setSelection(selection.index + 2);
        }
      }
    };

    editorRoot.addEventListener("keydown", handleKeyDown);

    return () => {
      editorRoot.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  return (
    <>
      <div className="modal-background">
        <div className="modal editor">
          <div className="topBar">
            <div>
              <select
                value={info.category}
                onChange={(e) =>
                  changeState(setInfo, "category", e.target.value)
                }
                disabled={isPreviewMode}
              >
                <option value="">카테고리 선택</option>
                <option value="상표">상표</option>
                <option value="비용및결제">비용 및 결제</option>
                <option value="상표모니터링">상표모니터링</option>
                <option value="회원정보">회원 정보</option>
                <option value="기타">기타</option>
                <option value="셀프상표출원">셀프 상표 출원</option>
                <option value="상표등록대행">상표 등록 대행</option>
                <option value="브랜드네이밍">브랜드 네이밍</option>
                <option value="상표등록가능성">상표 등록 가능성</option>
                <option value="위조상품/상표모니터링">
                  위조상품 · 무단 상표 선점
                </option>

              </select>
              <input
                type="text"
                placeholder="제목을 입력하세요"
                value={info.question}
                onChange={(e) =>
                  changeState(setInfo, "question", e.target.value)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                  }
                }}
                disabled={isPreviewMode}
              />
              <div className="viewBytes">
                <span>{byte.question}</span>/{300}
              </div>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button
                type="button"
                onClick={togglePreviewMode}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  color: isPreviewMode ? "#007bff" : "#666",
                  display: "flex",
                  alignItems: "center",
                  gap: "5px",
                }}
                title={
                  isPreviewMode ? "편집 모드로 전환" : "미리보기 모드로 전환"
                }
              >
                {isPreviewMode ? <FaEdit /> : <FaEye />}
                {isPreviewMode ? "편집" : "미리보기"}
              </button>
              <div onClick={handleCloseModal} style={{ cursor: "pointer" }}>
                <FaWindowClose />
              </div>
            </div>
          </div>
          <hr />

          {/* 에디터 - 항상 렌더링되지만 미리보기 모드에서는 숨김 */}
          <div
            className="quill-wrap"
            style={{
              display: isPreviewMode ? "none" : "block", // CSS로 숨기기/보이기
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.stopPropagation();
                e.preventDefault();
              }
            }}
          >
            {mode === "edit" && selectedFaq ? (
              // 수정 모드: value 없이 렌더링
              <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={modules}
                formats={formats}
                onChange={(c, d, s, editor) =>
                  changeState(setInfo, "answer", editor.getContents())
                }
              />
            ) : (
              // 새 작성 모드: value 사용
              <ReactQuill
                ref={quillRef}
                theme="snow"
                modules={modules}
                formats={formats}
                value={info?.answer}
                onChange={(c, d, s, editor) =>
                  changeState(setInfo, "answer", editor.getContents())
                }
              />
            )}
          </div>

          {/* 미리보기 모드 - 조건부 렌더링 */}
          {isPreviewMode && (
            <div
              className="preview-wrap"
              style={{
                flex: 1,
                width: "100%",
                overflow: "auto",
                padding: "20px",
                backgroundColor: "#f9f9f9",
                border: "1px solid #ddd",
                borderRadius: "4px",
                margin: "0",
              }}
            >
              <div className="preview-content">
                {info.category && (
                  <div
                    style={{
                      backgroundColor: "#e3f2fd",
                      color: "#1976d2",
                      padding: "8px 12px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      marginBottom: "15px",
                      display: "inline-block",
                    }}
                  >
                    {info.category}
                  </div>
                )}

                <div
                  style={{
                    backgroundColor: "white",
                    padding: "20px",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    minHeight: "200px",
                    lineHeight: "1.6",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "3px",
                      marginBottom: "20px",
                    }}
                  >
                    <span
                      style={{
                        fontWeight: "bold",
                        marginRight: "4px",
                        color: "#319dff",
                        fontSize: "24px",
                      }}
                    >
                      Q.
                    </span>
                    <span style={{ fontSize: "20px" }}>
                      {info.question ? info.question : "제목을 입력해주세요"}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "start",
                      gap: "5px",
                    }}
                    className="preview-answer-wrap"
                  >
                    <span
                      style={{
                        fontWeight: "bold",
                        marginRight: "4px",
                        color: "#858585",
                        fontSize: "20px",
                      }}
                    >
                      A.
                    </span>
                    <span
                      className="preview-answer"
                      dangerouslySetInnerHTML={{
                        __html:
                          getPreviewContent() ||
                          "<p style='color: #999; font-style: italic;'>내용을 입력해주세요</p>",
                      }}
                      style={{ fontSize: "18px" }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    marginTop: "15px",
                    fontSize: "12px",
                    color: info.is_active ? "#4caf50" : "#f44336",
                    fontWeight: "bold",
                  }}
                >
                  {info.is_active ? "✓ 노출됨" : "✗ 노출되지 않음"}
                </div>
              </div>
            </div>
          )}

          <div className="footer">
            <div className="viewBytes">
              <span>{byte.answer}</span>/3000
            </div>
            <div>
              <label style={{ marginRight: "20px", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={info.is_active}
                  onChange={(e) =>
                    changeState(setInfo, "is_active", e.target.checked)
                  }
                  style={{ marginRight: "5px" }}
                  disabled={isPreviewMode}
                />
                노출
              </label>
              <button type="button" onClick={handleCloseModal}>
                취소
              </button>
              <button type="button" onClick={handleSubmit}>
                완료
              </button>
            </div>
          </div>
        </div>
      </div>

      {localAlertBox.bool && (
        <CommonModal
          setModal={setLocalAlertBox}
          modal={localAlertBox}
          okFn={() => {
            if (
              localAlert === "completeEdit" ||
              localAlert === "completeApply"
            ) {
              setOpenWriteFaq(false);
              getList();
            } else if (localAlert === "confirmCancel") {
              setOpenWriteFaq(false);
            } else if (localAlert === "logout") {
              navigate("/");
            } else {
              return;
            }
          }}
        />
      )}
    </>
  );
};

export default FaqWriteModal;
